import { useQuery } from '@apollo/client'
import { Typography, Box } from '@mui/material'
import { ColumnsType } from 'antd/es/table'
import { DatePicker, Select, Space, Row, Col, Input, Tooltip } from 'antd'
import { useState, useEffect } from 'react'
import { GET_ALL_ORGANIZATIONS, GET_SUPER_ADMIN_LEADERBOARD, GET_USER_BY_ID } from '../../Graphql/Queries'
import Card from '../Cards/Card'
import DataTable from '../Datatable'
import Stats from '../Stats'
import TableHeading from '../TableHeading'
import dayjs from 'dayjs'
import Grid2 from '@mui/material/Unstable_Grid2/Grid2'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { useNavigate } from 'react-router-dom'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const { RangePicker } = DatePicker
const { Option } = Select

const SuperAdminDashboard = () => {
  const [tableData, setTableData] = useState<any[]>([])
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [selectedOrg, setSelectedOrg] = useState<string>('all')
  const [dateRange, setDateRange] = useState<any>(null)
  const navigate = useNavigate()
  const [selectedUser, setSelectedUser] = useState<any>(null)

  // Search states
  const [searchName, setSearchName] = useState<string>('');
  const [searchOrg, setSearchOrg] = useState<string>('');
  const [searchBatch, setSearchBatch] = useState<string>('');
  const [searchRank, setSearchRank] = useState<number | null>(null);
  const [searchTrainings, setSearchTrainings] = useState<number | null>(null);
  const [searchCompleted, setSearchCompleted] = useState<number | null>(null);
  const [searchAccuracy, setSearchAccuracy] = useState<number | null>(null);
  const [searchAvgTime, setSearchAvgTime] = useState<number | null>(null);
  const [searchCompletion, setSearchCompletion] = useState<number | null>(null);

  // Query to get all organizations for the dropdown
  const { data: orgData, loading: orgLoading } = useQuery<any>(GET_ALL_ORGANIZATIONS, {
    variables: {
      page: 1,
      limit: 50
    },
  });

  // Query to get leaderboard data
  const { loading: leaderBoardLoading, refetch } = useQuery<any>(GET_SUPER_ADMIN_LEADERBOARD, {
    variables: {
      orgId: selectedOrg === 'all' ? null : selectedOrg,
      startDate: dateRange && dateRange[0] ? dateRange[0].format('YYYY-MM-DD') : dayjs().startOf('month').format('YYYY-MM-DD'),
      endDate: dateRange && dateRange[1] ? dateRange[1].format('YYYY-MM-DD') : dayjs().endOf('month').format('YYYY-MM-DD'),
      name: searchName || undefined,
      organizationName: searchOrg || undefined,
      accuracy: searchAccuracy ? Number(searchAccuracy) : undefined,
      totalTrainings: searchTrainings ? Number(searchTrainings) : undefined,
      trainingCompleted: searchCompleted ? Number(searchCompleted) : undefined,
      rank: searchRank ? Number(searchRank) : undefined,
      batchName: searchBatch || undefined,
      avgTimeTaken: searchAvgTime ? Number(searchAvgTime) : undefined,
      completionPercent: searchCompletion ? Number(searchCompletion) : undefined,
    },
    onCompleted(data) {
      if (data.superAdminLeaderBoard?.length > 0) {
        const topRankers = data.superAdminLeaderBoard
        const mappedRankers = topRankers.map((ranker: any, index: number) => ({
          key: index,
          userId: ranker.userId,
          userName: ranker.name,
          rank: ranker.rank,
          noOfTrainings: ranker.totalTrainings,
          trainingCompleted: ranker.trainingCompleted,
          accuracy: ranker.accuracy,
          batchName: ranker.batchName,
          organizationName: ranker.organizationName,
          averageTimeTaken: ranker.avgTimeTaken, // <-- use new API field
          completionPercent: ranker.completionPercent, // keep as is if API field is the same
        }))
        setTableData(mappedRankers)
      } else {
        setTableData([])
      }
    },
  });

  // Add the GET_USER_BY_ID query
  const { data: userData, loading: userLoading } = useQuery(GET_USER_BY_ID, {
    variables: { userWithRolesByIdId: selectedUser?.userId },
    skip: !selectedUser?.userId,
    onCompleted(data) {
      if (data?.userWithRolesById) {
        // Navigate to user details page with the user data
        navigate(`/users/${selectedUser.userId}`, { state: { userData: data.userWithRolesById } })
      }
    },
    onError(error) {
      console.error('Error fetching user details:', error)
    }
  })

  // Effect to refetch data when search parameters change
  useEffect(() => {
    refetch();
  }, [
    searchName, searchOrg, searchBatch, searchRank, searchTrainings, searchCompleted, searchAccuracy,
    searchAvgTime, searchCompletion, // <-- add these
    selectedOrg, dateRange
  ]);

  const columns: ColumnsType<any> = [
    {
      title: (
        <TableHeading
          heading='Name'
          setSearch={setSearchName}
          setPage={setCurrentPage}
        />
      ),
      dataIndex: 'userName',
      key: '1',
      width: 160,
      ellipsis: true,
      render: (text) => (
        <Typography variant='body1' sx={{ textAlign: 'left' }}>
          {text}
        </Typography>
      ),
    },
    {
      title: (
        <TableHeading
          heading='Organization'
          setSearch={setSearchOrg}
          setPage={setCurrentPage}
        />
      ),
      dataIndex: 'organizationName',
      render: (_: unknown, record: { organizationName: string }) => (
        <Tooltip title={record.organizationName} arrow>
          <Typography
            variant="body1"
            sx={{
              textAlign: "left",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: 150,
              display: "block",
            }}
          >
            {record.organizationName || "N/A"}
          </Typography>
        </Tooltip>
      ),
      key: '2',
      width: 180,
    },
    {
        title: (
          <TableHeading
            heading='Batch'
            setSearch={setSearchBatch}
            setPage={setCurrentPage}
          />
        ),
        dataIndex: 'batchName',
        render: (_: unknown, record: { batchName: string }) => (
          <Tooltip title={record.batchName} arrow>
            <Typography
              variant="body1"
              sx={{
                textAlign: "left",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: 115,
                display: "block",
              }}
            >
              {record.batchName || "N/A"}
            </Typography>
          </Tooltip>
        ),
        key: '7',
        width: 150
    },
    {
      title: (
        <TableHeading
          heading='Rank'
          setSearch={setSearchRank}
          setPage={setCurrentPage}
        />
      ),
      dataIndex: 'rank',
      key: '3',
      width: 110,
      render: (text) => (
        <Typography variant='body1' sx={{ textAlign: 'left' }}>
          {text}
        </Typography>
      ),
    },
    {
      title: (
        <TableHeading
          heading='Number of trainings'
          setSearch={setSearchTrainings}
          setPage={setCurrentPage}
        />
      ),
      dataIndex: 'noOfTrainings',
      key: '4',
      width: 180,
      render: (text) => (
        <Typography variant='body1' sx={{ textAlign: 'left' }}>
          {text}
        </Typography>
      ),
    },
    {
      title: (
        <TableHeading
          heading='Trainings completed'
          setSearch={setSearchCompleted}
          setPage={setCurrentPage}
        />
      ),
      dataIndex: 'trainingCompleted',
      key: '5',
      width: 180,
      render: (text) => (
        <Typography variant='body1' sx={{ textAlign: 'left' }}>
          {text}
        </Typography>
      ),
    },
    {
      title: <TableHeading heading='Accuracy' setSearch={setSearchAccuracy} setPage={setCurrentPage} />, 
      dataIndex: 'accuracy',
      key: '6',
      width: 120,
      render: (value) => <Typography variant='body1'>{`${parseFloat(value).toFixed(2)}%`}</Typography>,
    },
    {
      title: <TableHeading heading='Average Time Taken' setSearch={setSearchAvgTime} setPage={setCurrentPage} />,
      dataIndex: 'averageTimeTaken',
      key: '7',
      width: 180,
      render: (value) => <Typography variant='body1'>{formatSecondsToMinSec(Number(value))}</Typography>,
    },
    {
      title: <TableHeading heading='Completion %' setSearch={setSearchCompletion} setPage={setCurrentPage} />,
      dataIndex: 'completionPercent',
      key: '8',
      width: 140,
      render: (value) => <Typography variant='body1'>{value !== undefined ? `${parseFloat(value).toFixed(2)}%` : 0}</Typography>,
    },
  ]

  const handleOrgChange = (value: string) => {
    setSelectedOrg(value)
  }

  const handleDateRangeChange = (dates: any, dateStrings: [string, string]) => {
    if (dates) {
      setDateRange([dates[0], dates[1]])
    } else {
      setDateRange(null)
    }
  }

  const handleRowClick = (record: any) => {
    navigate(`/users/${record.userId}`, { state: { userId: record.userId } });
  }

  const formatSecondsToMinSec = (totalSeconds: number) => {
    if (!totalSeconds || isNaN(totalSeconds)) return "0 sec";
    const mins = Math.floor(totalSeconds / 60);
    const secs = Math.round(totalSeconds % 60);
    if (mins > 0) {
      return `${mins} min${mins > 1 ? "s" : ""} ${secs > 0 ? `& ${secs} sec${secs > 1 ? "s" : ""}` : ""}`;
    }
    return `${secs} sec${secs > 1 ? "s" : ""}`;
  };
 
  return (
    <>
      <Row gutter={16} style={{ marginBottom: 16, alignItems: 'center' }}>
        <Col span={8}>
          <Stats text="Total Users" number={tableData.length} />
        </Col>
        <Col span={12} offset={4} >
          <div
            style={{
              display: 'flex',
              gap: '16px',
              alignItems: 'center',
              padding: '10px',
              border: '1px solid #d9d9d9',
              borderRadius: '8px',
              backgroundColor: 'rgb(248 248 248)',
              height: '70px', 
              marginTop:'50px'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
              <CalendarMonthIcon style={{ color: '#5c5a55' }} /> 
              <RangePicker
                onChange={handleDateRangeChange}
                value={dateRange || [dayjs().startOf('month'), dayjs().endOf('month')]}
                allowClear={true}
                format="YYYY-MM-DD"
                disabledDate={(current) => current && current > dayjs().endOf('day')}
                style={{ flex: 1 }} // Adaptive width for the calendar
                suffixIcon={null} 
              />
            </div>
            {/* Divider */}
            <div style={{ width: '1px', height: '32px', backgroundColor: '#d9d9d9' }}></div>
            <Select
              defaultValue="all"
              style={{ flex: 1 }} // Adaptive width for the dropdown
              onChange={handleOrgChange}
              value={selectedOrg}
              loading={orgLoading}
            >
              <Option value="all">All Organizations</Option>
              {orgData?.allOrganizations?.organizations?.map((org: any) => (
                <Option key={org._id} value={org._id}>
                  {org.name}
                </Option>
              ))}
            </Select>
          </div>
        </Col>
      </Row>

      {/* Info icon and formula explanation */}
      <Box display="flex" alignItems="center" mb={1} justifyContent="flex-end">
        <Tooltip
          title={
            <span>
              <b>Rank Score Formula:</b><br />
              <code>
                Score = 0.5 × (accuracy / 100) + 0.3 × (completionPercent / 100) + 0.2 × (1 - (avgTimeTaken / Tmax))
              </code>
              <br />
              <span>
                <b>Note:</b> Tmax is the maximum average time taken among all users.
              </span>
            </span>
          }
          placement="left"
        >
          <InfoOutlinedIcon sx={{ mr: 1, cursor: 'pointer', color: '#888' }} />
        </Tooltip>
      </Box>

      <Card>
        <DataTable
          loading={leaderBoardLoading}
          tableData={tableData}
          showCheck={false}
          columnData={columns}
          scrollX='max-content'
          onRow={(record) => handleRowClick(record)}
        />
      </Card>
    </>
  )
}

export default SuperAdminDashboard